/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { InitiativePlain, InitiativeTags } from '../types/initiative';
import { InitiativeCompany } from '@g17eco/types/initiativeSurvey';
import { isAfter, isBefore } from './date';
import { SubscriptionService } from '../services/SubscriptionService';
import { DataShareStatus } from '../types/dataShare';
import { CurrentUserData } from '../reducers/current-user';
import { ChildrenTree, Tree } from '../types/tree';
import { ProductCodes } from '../types/products';

interface CompanyStatusResponse {
  onboarded: boolean;
  survey_created: boolean;
  survey_completed: boolean;
  data_shared: boolean;
  is_verified: boolean;
  is_premium: boolean;
  status: 'active' | 'inactive' | 'invited';
}

export type CompanyStatusProps = Pick<InitiativeCompany,
  'userCount' |
  'lastCompletedSurveyDate' |
  'lastSurveyDate' |
  'requestedDataShares' |
  'customer' |
  'tags'
>;

export const getCompanyStatus = (company: CompanyStatusProps): CompanyStatusResponse => {

  const hasActiveSubscription = (
    SubscriptionService.hasActive(ProductCodes.CompanyTracker, company.customer?.subscriptions)
    ||
    SubscriptionService.hasActive(ProductCodes.SGXESGenome, company.customer?.subscriptions)
  );

  const requestedDataShares = company.requestedDataShares;
  const hasAcceptedDataShare = requestedDataShares?.some(s => s.status === DataShareStatus.Accepted);

  const cutoffDate = new Date();
  cutoffDate.setFullYear(new Date().getFullYear() - 2);

  const report = {
    onboarded: company.userCount > 0,
    survey_created: company.lastSurveyDate ? isBefore(company.lastSurveyDate, cutoffDate) : false,
    survey_completed: company.lastCompletedSurveyDate ? isBefore(company.lastCompletedSurveyDate, cutoffDate) : false,
    data_shared: Boolean(hasAcceptedDataShare),
    is_verified: company.tags?.includes(InitiativeTags.VerifiedOrganization) ?? false,
    is_premium: hasActiveSubscription,
  }

  const isActive =
    report.is_verified &&
    report.is_premium &&
    report.survey_created &&
    report.survey_completed &&
    report.data_shared

  return {
    ...report,
    status: isActive ? 'active' : report.onboarded ? 'inactive' : 'invited'
  };
}

export class InitiativeTree {
  static isDescendant(user: Pick<CurrentUserData, 'initiativeTree'>, descendantId: string, parentId: string, infiniteRecursionProtection: string[] = []): boolean {
    infiniteRecursionProtection.push(parentId);
    const initiativeTree = user.initiativeTree;
    if (!initiativeTree) {
      return false;
    }

    if (descendantId === parentId) {
      return true;
    }

    const initiative = initiativeTree.find(d => d._id === descendantId);
    if (!initiative?.parentId) {
      return false;
    }

    if (initiative.parentId === parentId) {
      return true;
    }

    if (infiniteRecursionProtection.includes(initiative.parentId)) {
      return false;
    }

    const parent = initiativeTree.find(d => d._id === initiative.parentId);
    if (!parent) {
      return false;
    }

    return this.isDescendant(user, parentId, parent._id, infiniteRecursionProtection);
  }
}

export const findRootParentInTree = (
  tree: Tree | ChildrenTree,
  id: string,
) => {
  if (tree.id === id) {
    return tree;
  }

  const rootParents = tree.children;

  if (!rootParents || rootParents.length === 0) {
    return tree;
  }

  for (const parent of rootParents) {
    const stack = [...parent.children ?? []];
    if (parent.id === id) {
      return parent;
    }
    while (stack && stack.length > 0) {
      const currentNode = stack.shift() as ChildrenTree;
      if (currentNode.id === id) {
        return parent;
      }
      currentNode?.children?.forEach((childNode) => stack.push(childNode));
    }
  }
  return;
}

export const findCurrentBranchInTree = (
  tree: Tree | ChildrenTree,
  id: string,
  branch: (Tree | ChildrenTree)[] = []
): { branch: (Tree | ChildrenTree)[]; node?: Tree | ChildrenTree } => {

  if (tree.id === id) {
    return { branch, node: tree };
  }

  if (tree.children && tree.children.length > 0) {
    const foundNode = tree.children.find((c) => {
      const found = findCurrentBranchInTree(c, id, branch);
      if (found.node) {
        found.branch.push(c);
      }

      return found.node;
    });
    return { branch, node: foundNode };
  }
  return { branch };
};

export const findAllChildrenById = (
  list: InitiativePlain[],
  parentId: string,
  currentLevel: number = 1
): Array<InitiativePlain & { level: number }> => {
  const descendants: Array<InitiativePlain & { level: number }> = [];

  const directChildren = list.filter((item) => item.parentId === parentId);

  for (const child of directChildren) {
    const childDescendants = findAllChildrenById(list, child._id, currentLevel + 1);
    descendants.push({ ...child, level: currentLevel }, ...childDescendants);
  }

  return descendants;
};

export const isDemo = (initiative: Pick<InitiativePlain, 'tags'>) => {
  return initiative.tags?.includes(InitiativeTags.DemoOrganization) ?? false;
}

export const isSingleTree = (initiativeTree: Tree | undefined) => {
  if (!initiativeTree) {
    return false;
  }
  return initiativeTree.children.length === 1 && initiativeTree?.children[0].children?.length === 0;
}

const WEIGHTED_AGGREGATION_AUTO_OPT_IN_CUTOFF = '2026-03-01';

export const shouldAutoOptInToWeightedAggregation = (created: string | number | Date | undefined) => {
  if (!created) {
    return false;
  }
  return isAfter(WEIGHTED_AGGREGATION_AUTO_OPT_IN_CUTOFF, created);
};
